import { Calendar, User, Target } from 'lucide-react';

export default function Campaigns() {
    const campaigns = [
        {
            topic: 'Support Education for Ethiopian Children',
            description: 'Join us in our mission to provide quality education to underprivileged children across Ethiopia. Every contribution helps build schools, provide educational materials, and support teachers in rural communities.',
            organizer: 'Ethiopian Education Foundation',
            logoUrl: 'https://images.pexels.com/photos/289737/pexels-photo-289737.jpeg?auto=compress&cs=tinysrgb&w=400',
            imageUrls: [
                'https://images.pexels.com/photos/8422087/pexels-photo-8422087.jpeg?auto=compress&cs=tinysrgb&w=600',
                'https://images.pexels.com/photos/8422124/pexels-photo-8422124.jpeg?auto=compress&cs=tinysrgb&w=600'
            ],
            startDate: '2025-01-01',
            endDate: '2025-12-31'
        },
        {
            topic: 'Clean Water Initiative - Rural Ethiopia',
            description: 'Help bring clean water to remote villages in Ethiopia. This campaign aims to install water wells and purification systems in 50 villages, providing access to safe drinking water for thousands of families.',
            organizer: 'Water For All Ethiopia',
            logoUrl: 'https://images.pexels.com/photos/1624438/pexels-photo-1624438.jpeg?auto=compress&cs=tinysrgb&w=400',
            imageUrls: [
                'https://images.pexels.com/photos/4223430/pexels-photo-4223430.jpeg?auto=compress&cs=tinysrgb&w=600',
                'https://images.pexels.com/photos/4223161/pexels-photo-4223161.jpeg?auto=compress&cs=tinysrgb&w=600'
            ],
            startDate: '2025-03-01',
            endDate: '2025-08-31'
        },
        {
            topic: 'Healthcare Access Program',
            description: 'Supporting rural healthcare facilities with medical equipment, medicine, and trained staff. Your donations help provide essential healthcare services to communities that need it most.',
            organizer: 'Ethiopian Medical Alliance',
            logoUrl: 'https://images.pexels.com/photos/127873/pexels-photo-127873.jpeg?auto=compress&cs=tinysrgb&w=400',
            imageUrls: [
                'https://images.pexels.com/photos/4386467/pexels-photo-4386467.jpeg?auto=compress&cs=tinysrgb&w=600',
                'https://images.pexels.com/photos/8460157/pexels-photo-8460157.jpeg?auto=compress&cs=tinysrgb&w=600'
            ],
            startDate: '2025-02-01',
            endDate: '2025-11-30'
        },
        {
            topic: 'Women Empowerment & Skills Training',
            description: 'Empowering Ethiopian women through vocational training, microfinance, and business development programs. Help women gain financial independence and support their families.',
            organizer: 'Ethiopian Women Development Network',
            logoUrl: 'https://images.pexels.com/photos/3184338/pexels-photo-3184338.jpeg?auto=compress&cs=tinysrgb&w=400',
            imageUrls: [
                'https://images.pexels.com/photos/3184639/pexels-photo-3184639.jpeg?auto=compress&cs=tinysrgb&w=600',
                'https://images.pexels.com/photos/3184632/pexels-photo-3184632.jpeg?auto=compress&cs=tinysrgb&w=600'
            ],
            startDate: '2025-01-15',
            endDate: '2025-12-15'
        }
    ];

    return (
        <div className="min-h-screen pt-24 pb-20">
            <section className="bg-gradient-to-br from-emerald-800 to-emerald-900 text-white py-20">
                <div className="container mx-auto px-4">
                    <div className="max-w-3xl mx-auto text-center">
                        <h1 className="text-5xl font-bold mb-6">
                            Active Campaigns
                        </h1>
                        <p className="text-xl text-emerald-50">
                            Support meaningful causes that make a difference in Ethiopian communities. Every contribution counts.
                        </p>
                    </div>
                </div>
            </section>

            <section className="py-12 bg-gray-50">
                <div className="container mx-auto px-4">
                    <div className="space-y-12">
                        {campaigns.map((campaign, index) => (
                            <div key={index} className="bg-white rounded-2xl shadow-xl overflow-hidden hover:shadow-2xl transition">
                                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                                    <div className="lg:col-span-1">
                                        <img
                                            src={campaign.logoUrl}
                                            alt={campaign.topic}
                                            className="w-full h-64 lg:h-full object-cover"
                                        />
                                    </div>

                                    <div className="lg:col-span-2 p-8">
                                        <div className="flex items-center gap-3 mb-4">
                                            <div className="bg-emerald-100 p-2 rounded-lg">
                                                <Target className="w-6 h-6 text-emerald-800" />
                                            </div>
                                            <h2 className="text-3xl font-bold text-emerald-900">{campaign.topic}</h2>
                                        </div>

                                        <p className="text-gray-600 mb-6 leading-relaxed text-lg">
                                            {campaign.description}
                                        </p>

                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                                            <div className="flex items-center gap-3 bg-emerald-50 p-4 rounded-lg">
                                                <User className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-700">Organized By</p>
                                                    <p className="text-emerald-900 font-semibold">{campaign.organizer}</p>
                                                </div>
                                            </div>

                                            <div className="flex items-center gap-3 bg-emerald-50 p-4 rounded-lg">
                                                <Calendar className="w-5 h-5 text-emerald-800 flex-shrink-0" />
                                                <div>
                                                    <p className="text-sm font-semibold text-gray-700">Campaign Period</p>
                                                    <p className="text-emerald-900 font-semibold">
                                                        {new Date(campaign.startDate).toLocaleDateString()} - {new Date(campaign.endDate).toLocaleDateString()}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-2 gap-4 mb-6">
                                            {campaign.imageUrls.map((imageUrl, imgIndex) => (
                                                <img
                                                    key={imgIndex}
                                                    src={imageUrl}
                                                    alt={`${campaign.topic} ${imgIndex + 1}`}
                                                    className="w-full h-40 object-cover rounded-lg"
                                                />
                                            ))}
                                        </div>

                                        <button className="w-full bg-yellow-500 text-emerald-900 px-8 py-4 rounded-lg text-lg font-semibold hover:bg-yellow-400 transition transform hover:scale-105">
                                            Support This Campaign
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <section className="py-12 bg-white">
                <div className="container mx-auto px-4">
                    <div className="max-w-4xl mx-auto text-center">
                        <h2 className="text-3xl font-bold text-emerald-900 mb-6">
                            Want to Start Your Own Campaign?
                        </h2>
                        <p className="text-lg text-gray-600 mb-8">
                            Partner with Mojo Money to launch your charitable campaign and reach the Ethiopian diaspora community worldwide.
                        </p>
                        <a
                            href="/contact"
                            className="inline-block bg-emerald-800 text-white px-8 py-4 rounded-lg text-lg font-semibold hover:bg-emerald-700 transition transform hover:scale-105"
                        >
                            Contact Us to Get Started
                        </a>
                    </div>
                </div>
            </section>
        </div>
    );
}
